import { ParsedUrlQuery } from 'querystring'
import { useCallback } from 'react'
import { CouponForEstimateOrderScheme, EstimateOrderResponseDtoV1 } from '@/apis/rest/generated/types'
import { FitpetMallEventEnum } from '@/constants/fitpet-mall-event.enum'
import { OrderTypeEnum } from '@/constants/order-type.enum'
import { decodeId } from '@/utils/utilApi'
import { useTracker } from '../contexts/EventTrackingProvider'
import { IModelCoupon } from '../models/modelCoupon'

const useOrderPageEventTracker = () => {
  const { triggerCustomEvent } = useTracker()

  /** 주문페이지에서 쿠폰 조회를 눌러 사용가능한 쿠폰 리스트를 조회했을 때 */
  const trackViewOrderCoupon = useCallback(
    (coupons: IModelCoupon[], totalProductAmount: number) => {
      const totalCouponQuantity = coupons.length
      const couponIds = coupons.map((coupon) => decodeId(coupon.couponGroup.id).id)
      const couponNames = coupons.map((coupon) => coupon.couponGroup.name)

      triggerCustomEvent(FitpetMallEventEnum.ViewOrderCoupon, {
        totalCouponQuantity,
        couponIds,
        couponNames,
        totalProductAmount,
      })
    },
    [triggerCustomEvent]
  )

  /** 주문페이지에서 쿠폰 조회를 눌러 사용가능한 쿠폰 리스트를 조회했을 때 */
  const trackViewOrderCouponNew = (coupons: CouponForEstimateOrderScheme[], totalProductAmount: number) => {
    const totalCouponQuantity = coupons.length
    const couponIds = coupons.map((coupon) => coupon.id.toString())
    const couponNames = coupons.map((coupon) => coupon.name)

    triggerCustomEvent(FitpetMallEventEnum.ViewOrderCoupon, {
      totalCouponQuantity,
      couponIds,
      couponNames,
      totalProductAmount,
    })
  }

  /** 주문 쿠폰 페이지에서 쿠폰을 선택해서 적용 완료를 클릭했을 때 */
  const trackClickOrderCouponApply = useCallback(
    (
      coupons: IModelCoupon[],
      selectedCoupon: IModelCoupon,
      totalProductAmount: number,
      couponDiscountAmount: number
    ) => {
      const appliedCouponId = decodeId(selectedCoupon.couponGroup.id).id
      const appliedCouponName = selectedCoupon.couponGroup.name
      const couponIds = coupons.map((coupon) => decodeId(coupon.couponGroup.id).id)
      const couponNames = coupons.map((coupon) => coupon.couponGroup.name)
      const totalCouponQuantity = coupons.length
      const daysToExpire = selectedCoupon.getDaysRemainingToUse()

      triggerCustomEvent(FitpetMallEventEnum.ClickOrderCouponApply, {
        appliedCouponId,
        appliedCouponName,
        daysToExpire,
        couponDiscountAmount,
        totalCouponQuantity,
        couponIds,
        couponNames,
        totalProductAmount,
      })
    },
    [triggerCustomEvent]
  )

  /** 주문 쿠폰 페이지에서 쿠폰을 선택해서 적용 완료를 클릭했을 때 */
  const trackClickOrderCouponApplyNew = (
    coupons: CouponForEstimateOrderScheme[],
    selectedCoupon: CouponForEstimateOrderScheme,
    totalProductAmount: number
  ) => {
    const appliedCouponId = selectedCoupon.id.toString()
    const appliedCouponName = selectedCoupon.name
    const couponIds = coupons.map((coupon) => coupon.id.toString())
    const couponNames = coupons.map((coupon) => coupon.name)
    const totalCouponQuantity = coupons.length
    const daysToExpire = selectedCoupon.daysOfRemaining
    const couponDiscountAmount = selectedCoupon.appliedDiscountAmount

    triggerCustomEvent(FitpetMallEventEnum.ClickOrderCouponApply, {
      appliedCouponId,
      appliedCouponName,
      daysToExpire,
      couponDiscountAmount,
      totalCouponQuantity,
      couponIds,
      couponNames,
      totalProductAmount,
    })
  }

  const trackViewOrder = ({
    estimateOrder,
    routerParam,
  }: {
    estimateOrder: EstimateOrderResponseDtoV1
    routerParam: { isReady: boolean; query: ParsedUrlQuery }
  }) => {
    if (!estimateOrder || !routerParam.isReady) {
      return
    }

    const products: {
      product_id: string
      product_name: string
      categories?: { category_id: string; category_name: string }[]
    }[] = []
    estimateOrder?.shippings?.forEach((shipping) => {
      shipping.orderItems?.forEach((orderItem) => {
        products.push({
          product_id: orderItem.productId.toString(),
          product_name: orderItem.productName,
          categories:
            orderItem?.category?.map((c) => {
              return { category_id: c.id.toString(), category_name: c.name }
            }) || [],
        })
      })
    })

    triggerCustomEvent(FitpetMallEventEnum.ViewOrder, {
      order_id: 'None',
      products,
      total_order_quantity: estimateOrder.quantityOfOrderItem,
      total_product_amount: estimateOrder.productSaleAmount,
      total_product_discount_amount: estimateOrder.discountAmount,
      total_shipment_amount: estimateOrder.shippingAmount,
      isSinglepurchase: routerParam.query?.orderType === OrderTypeEnum.BuyNow,
    })
  }

  return {
    trackViewOrderCoupon,
    trackClickOrderCouponApply,
    trackViewOrder,
    trackViewOrderCouponNew,
    trackClickOrderCouponApplyNew,
  }
}

export default useOrderPageEventTracker
